import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, ArrowRight } from 'lucide-react';

export const HeroSection = () => (
  <section
    className="relative overflow-hidden bg-white"
    data-testid="hero-section"
  >
    {/* Subtle grid background */}
    <div
      className="absolute inset-0 opacity-[0.04] pointer-events-none"
      style={{
        backgroundImage:
          'linear-gradient(#0F0F0F 1px, transparent 1px), linear-gradient(90deg, #0F0F0F 1px, transparent 1px)',
        backgroundSize: '48px 48px',
      }}
    />

    {/* Animated soft-colour blobs (behind grid) */}
    <div
      className="hero-blob hero-blob-orange absolute -top-32 -left-20 w-[560px] h-[560px] rounded-full blur-3xl pointer-events-none"
      style={{ background: 'radial-gradient(closest-side, rgba(255,69,26,0.08), transparent)' }}
      aria-hidden="true"
    />
    <div
      className="hero-blob hero-blob-gold absolute top-1/3 -right-32 w-[520px] h-[520px] rounded-full blur-3xl pointer-events-none"
      style={{ background: 'radial-gradient(closest-side, rgba(201,151,74,0.06), transparent)' }}
      aria-hidden="true"
    />
    <div
      className="hero-blob hero-blob-orange-2 absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none"
      style={{ background: 'radial-gradient(closest-side, rgba(255,69,26,0.05), transparent)' }}
      aria-hidden="true"
    />

    <style>{`
      @keyframes hero-drift-a {
        0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
        50%      { transform: translate3d(60px, 40px, 0) scale(1.08); }
      }
      @keyframes hero-drift-b {
        0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
        50%      { transform: translate3d(-50px, -30px, 0) scale(1.10); }
      }
      @keyframes hero-drift-c {
        0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
        50%      { transform: translate3d(30px, -40px, 0) scale(1.06); }
      }
      .hero-blob-orange   { animation: hero-drift-a 9s ease-in-out infinite; }
      .hero-blob-gold     { animation: hero-drift-b 11s ease-in-out infinite; }
      .hero-blob-orange-2 { animation: hero-drift-c 10s ease-in-out infinite; }
      @media (prefers-reduced-motion: reduce) {
        .hero-blob { animation: none !important; }
      }
    `}</style>

    <div className="relative max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-20 md:pb-28">
      {/* Path selector cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-14 md:mb-20 max-w-4xl mx-auto">
        <PathCard
          icon={Building2}
          heading="I'm a Brand"
          body="Find, manage and track influencer campaigns — all in one place"
          testid="hero-path-brand"
        />
        <PathCard
          icon={Users}
          heading="I'm an Agency"
          body="Run campaigns for multiple clients with full visibility and control"
          testid="hero-path-agency"
        />
      </div>

      {/* Headline block */}
      <div className="text-center max-w-4xl mx-auto">
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0F0F0F] leading-[1.05]"
          data-testid="hero-headline"
        >
          India&apos;s Creator Economy.
          <br />
          <span className="text-[#FF451A]">Managed End to End.</span>
        </h1>
        <p
          className="mt-6 text-base md:text-lg text-[#4B5563] max-w-2xl mx-auto leading-relaxed"
          data-testid="hero-subline"
        >
          From discovering the right creators to getting content approved, addresses collected, and invoices closed —
          without a single WhatsApp thread.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/demo"
            className="w-full sm:w-auto px-6 py-3 rounded-full text-sm font-semibold bg-[#FF451A] text-white hover:bg-[#E63E17] transition-colors shadow-[0_6px_24px_rgba(255,69,26,0.28)] inline-flex items-center justify-center gap-2"
            data-testid="hero-request-demo"
          >
            Request Demo <ArrowRight size={14} />
          </Link>
          <Link
            to="/login"
            className="w-full sm:w-auto px-6 py-3 rounded-full text-sm font-semibold border border-black/10 text-[#0F0F0F] hover:bg-black/[0.03] transition-colors inline-flex items-center justify-center"
            data-testid="hero-sign-in"
          >
            Sign In
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const PathCard = ({ icon: Icon, heading, body, testid }) => (
  <button
    onClick={() => { window.location.href = '/login'; }}
    className="text-left group bg-white border border-black/[0.08] rounded-2xl p-6 hover:border-[#FF451A]/60 hover:shadow-[0_10px_40px_rgba(255,69,26,0.08)] transition-all"
    data-testid={testid}
  >
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#FF451A]/10 flex items-center justify-center shrink-0 group-hover:bg-[#FF451A] transition-colors">
        <Icon size={20} className="text-[#FF451A] group-hover:text-white transition-colors" />
      </div>
      <div className="flex-1">
        <div className="text-lg font-semibold text-[#0F0F0F]">{heading}</div>
        <div className="text-sm text-[#4B5563] mt-1 leading-relaxed">{body}</div>
      </div>
      <ArrowRight
        size={18}
        className="text-[#0F0F0F]/40 group-hover:text-[#FF451A] group-hover:translate-x-0.5 transition-all shrink-0 mt-1"
      />
    </div>
  </button>
);

export default HeroSection;
